import { useState, useEffect } from 'react';
import { Building2, FileText, Calendar, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClientDashboard({ user }) {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRentals = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/rentals/my-properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRentals(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement de vos biens');
      } finally {
        setLoading(false);
      }
    };

    fetchRentals();
  }, []);

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="client-dashboard" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="pt-12 space-y-4">
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-none">Mon espace</h1>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
            Bienvenue {user.full_name}
          </p>
        </div>

        {rentals.length === 0 ? (
          <div data-testid="no-rentals" className="bg-secondary p-12 text-center">
            <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-serif mb-2">Aucun bien en gestion</h3>
            <p className="text-muted-foreground">Vous n'avez actuellement aucun bien géré par notre agence</p>
          </div>
        ) : (
          <div className="space-y-8">
            <h2 className="text-3xl font-serif tracking-tight">Mes biens en gestion</h2>
            
            <div data-testid="rentals-list" className="grid grid-cols-1 gap-6">
              {rentals.map((rental) => (
                <div key={rental.id} data-testid={`rental-card-${rental.id}`} className="bg-white border border-border p-6 hover:shadow-md transition-all duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-4">
                      <div className="flex items-start gap-4">
                        {rental.property?.images?.[0] && (
                          <img
                            src={rental.property.images[0]}
                            alt={rental.property.title}
                            className="w-32 h-24 object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h3 data-testid="rental-property-title" className="text-2xl font-serif tracking-tight mb-2">
                            {rental.property?.title || 'Bien immobilier'}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">{rental.property?.location}</p>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span>Début: {new Date(rental.start_date).toLocaleDateString('fr-BE')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-baseline gap-3 pt-4 border-t border-border">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Loyer mensuel</div>
                        <div data-testid="rental-amount" className="text-2xl font-serif font-bold text-accent">€{rental.rent_amount.toLocaleString()}</div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-3">Documents</h4>
                        {rental.documents && rental.documents.length > 0 ? (
                          <div className="space-y-2">
                            {rental.documents.map((doc, idx) => (
                              <button
                                key={idx}
                                data-testid={`document-${idx}`}
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                              >
                                <Download className="w-4 h-4" />
                                {doc.name || `Document ${idx + 1}`}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Aucun document disponible</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-3">Paiements</h4>
                        {rental.payments && rental.payments.length > 0 ? (
                          <div className="space-y-2">
                            {rental.payments.slice(0, 3).map((payment, idx) => (
                              <div key={idx} data-testid={`payment-${idx}`} className="text-sm">
                                <div className="flex justify-between">
                                  <span>{new Date(payment.date).toLocaleDateString('fr-BE')}</span>
                                  <span className="font-semibold">€{payment.amount}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Aucun paiement enregistré</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDashboard;